import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { BreadcrumbItem } from "@/types/breadcrumb";
import AddItemButton from "./AddItemButton";
import { DataTableQueryContextProvider } from "./data-table/DataTableQueryContext";
import { QueryTable } from "./query-table/QueryTable";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useShowDeletedToggle } from "@/hooks/useShowDeletedToggle";
import { useCallback, useMemo } from "react";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { SuccessFetchListResponse } from "@/services/api";
import { Filters } from "@/lib/filters";
import { ColumnDef, ColumnSort } from "@tanstack/react-table";
import { ZodRawShape } from "zod";
import { ZodObject } from "zod";
import { ZodReadonly } from "zod";
import { DataTableFeatures } from "@/types/dataTableSettings";
import PermissionGuard from "./PermissionGuard";
import { Operation } from "@/types/permissions";

interface StandardQueryTableProps<T extends { id: string }> {
  schema: ZodReadonly<ZodObject<ZodRawShape>>;
  name: string;
  columnOverrides?: Record<string, ColumnDef<T, number>>;
  tableFeatures?: DataTableFeatures;
  actions?: React.ReactNode[];
  fetchList: (params: {
    lastItem: string | undefined;
    filters: Filters<T> | undefined;
    sorting: ColumnSort | undefined;
    queryParams?: Record<string, string>;
  }) => Promise<SuccessFetchListResponse<T>>;
  breadcrumbs?: BreadcrumbItem[];
  headerContent?: React.ReactNode;
  menu?: React.ReactNode;
  translationKey?: string;
  /**
   * Custom function to build the path for each row's link.
   */
  pathBuilder?: (row: T) => string;
}

export default function StandardQueryTable<T extends { id: string }>({
  schema,
  name,
  fetchList,
  columnOverrides,
  tableFeatures,
  pathBuilder,
  actions = [
    pathBuilder && (
      <PermissionGuard permission={Operation.Create}>
        <AddItemButton
          key="add"
          path={
            pathBuilder?.({
              id: "new",
            } as unknown as T) as string
          }
          labelKey={`${name}.add`}
        />
      </PermissionGuard>
    ),
  ],
  breadcrumbs,
  headerContent,
  menu,
  translationKey,
}: StandardQueryTableProps<T>) {
  const options = useCreateDataTableOptions<T>(name);

  const { queryKeyExtras, queryParams, toggleItem } = useShowDeletedToggle(
    name,
    !!tableFeatures?.enableShowDeletedToggle,
  );

  const queryKey = useDynamicQueryParams(name, options, queryKeyExtras);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
        ...(queryParams && { queryParams }),
      }),
    [fetchList, options.filters, options.sorting, queryParams],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<T>) => lastPage?.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  return (
    <QueryTable
      schema={schema}
      name={name}
      columnOverrides={columnOverrides}
      query={query}
      options={options}
      tableFeatures={tableFeatures}
      translationKey={translationKey}
      pathBuilder={pathBuilder}
    >
      <QueryTable.Header
        breadcrumbLabel={`${name}.title.multiple`}
        breadcrumbs={breadcrumbs}
        actions={actions}
      />
      {headerContent && <div className="py-2">{headerContent}</div>}
      <QueryTable.Menubar>
        <QueryTable.Menubar.Tools />
        <QueryTable.Menubar.Columns />
        <QueryTable.Menubar.Settings>
          {menu}
          {toggleItem}
        </QueryTable.Menubar.Settings>
        <QueryTable.Menubar.OnlySelected />
      </QueryTable.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <QueryTable.Content>
          <QueryTable.Status />
        </QueryTable.Content>
      </DataTableQueryContextProvider>
    </QueryTable>
  );
}
